// @ts-nocheck
/**
 * qrCodeRenderer.ts — Template V2 layout QR renderer (DEC-082).
 *
 * Resolves payload + colors, generates SVG synchronously, and bakes freeze-safe
 * props (`svgContent`, value, colors, ECL, fit). Empty/invalid payloads emit a
 * placeholder (svgContent null).
 */

import type { ValueSource } from '../../types/binding';
import type { QrCodeErrorCorrection } from '../../types/component';
import { applyExpressionFallback, buildDescriptor, resolveValue } from './rendererUtils';
import type { CompiledPropsByType, ComponentRenderer } from './types';
import { extractTypedProps } from './types';
import {
  DEFAULT_QR_BACKGROUND_COLOR,
  DEFAULT_QR_ERROR_CORRECTION,
  DEFAULT_QR_MODULE_COLOR,
  generateQrSvg,
  isQrColorString,
  normalizeQrErrorCorrection,
} from '../qr/generateQrSvg';

function resolveQrColor(
  source: ValueSource<string> | undefined,
  fallback: string,
  fieldLabel: 'color' | 'backgroundColor',
  context: Parameters<ComponentRenderer<CompiledPropsByType['qrCode']>['render']>[0],
): string {
  if (source == null) {
    return fallback;
  }
  const raw = resolveValue(source, context);
  if (raw == null || raw === '') {
    return fallback;
  }
  if (typeof raw === 'string' && isQrColorString(raw)) {
    return raw;
  }
  context.issues.push({
    severity: 'warning',
    code: 'schema.invalid_field_type',
    message:
      `QR ${fieldLabel} "${String(raw)}" is not a valid CSS color for node "${context.node.id}"; ` +
      `using ${fallback}.`,
    path: context.node.id,
  });
  return fallback;
}

export const QR_CODE_RENDERER: ComponentRenderer<CompiledPropsByType['qrCode']> = {
  supportedType: 'qrCode',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('qrCode', context.node.props);
    const bag = p.qrCode;
    const source = bag?.source ?? { kind: 'static' as const, value: '' };
    const rawPayload = resolveValue(source, context);
    const value =
      rawPayload == null
        ? ''
        : typeof rawPayload === 'string'
          ? rawPayload
          : String(rawPayload);
    const color = resolveQrColor(bag?.color, DEFAULT_QR_MODULE_COLOR, 'color', context);
    const backgroundColor = resolveQrColor(
      bag?.backgroundColor,
      DEFAULT_QR_BACKGROUND_COLOR,
      'backgroundColor',
      context,
    );
    const errorCorrection: QrCodeErrorCorrection = normalizeQrErrorCorrection(bag?.errorCorrection);
    const fit = bag?.fit === 'cover' || bag?.fit === 'fill' ? bag.fit : 'contain';

    const svgContent = generateQrSvg(value, { color, backgroundColor, errorCorrection });
    const placeholder = svgContent == null;
    if (placeholder && value.trim().length > 0) {
      context.issues.push({
        severity: 'warning',
        code: 'schema.invalid_field_type',
        message: `QR payload could not be encoded for node "${context.node.id}".`,
        path: context.node.id,
      });
    }

    return buildDescriptor(
      context.node,
      visible,
      enabled,
      {
        value,
        color,
        backgroundColor,
        errorCorrection: errorCorrection || DEFAULT_QR_ERROR_CORRECTION,
        fit,
        svgContent,
        placeholder,
      },
      { diagnostics: context.issues.slice(issuesBefore), renderContext: context },
    );
  },
};
